import * as React from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import { EditInBulkPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import type { RouteContextParams } from 'modules/employees/lib/RouteContextParams'
import EditInBulk from 'modules/employees/pages/EditInBulk'
import employees from 'modules/employees/store/collections/employees'
import type { Employees } from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = {
  employeesIds?: number[]
  selectedLegalEntity?: string
  importType?: string
} | null

const useEmployees = createComputed((_employees: Employees, selectedIds: Set<number>) => {
  if (!selectedIds.size) return _employees.toArray()

  return _employees.toArray().filter((employee) => selectedIds.has(employee.attributes.id))
})

const EditInBulkPage = () => {
  const { filtersForm } = useOutletContext<RouteContextParams>()
  const {
    company,
    access: { locale, id: currentUser },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedIds = new Set(locationState?.employeesIds ?? [])
  const selectedLegalEntityId = locationState?.selectedLegalEntity
  const importType = locationState?.importType

  const nuvo_import_type =
    importType === 'employees' ? 'employee_edition' : 'contract_version_edition'

  const { data, loading, error } = useQuery(EditInBulkPageDocument, {
    variables: {
      companyId: company.id,
      legalEntityId: Number(selectedLegalEntityId),
    },
    skip: !selectedLegalEntityId,
  })

  const legalEntityCountry = data?.companies.legalEntitiesConnection.nodes[0]?.country
  const legalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [Number(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const metadata = {
    legal_entity_ids: legalEntitiesIds,
    employee_ids: Array.from(selectedIds),
    legal_entity_countries: legalEntityCountry ? [legalEntityCountry] : [],
  }

  const tracker_id = crypto.randomUUID()

  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser,
      company_id: company.id,
      legal_entity_ids: legalEntitiesIds,
      import_type: nuvo_import_type,
      tracker_id: tracker_id,
    }),
    [currentUser, company.id, legalEntitiesIds, nuvo_import_type, tracker_id]
  )

  const universalEditInBulk = data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_universal_employees_edition'
  )?.enabled

  const canImportResources = data?.universalImporter._policies.importResources.can

  const authorized = universalEditInBulk === true && canImportResources

  const selectedEmployees = useEmployees(employees, selectedIds).get()

  const canViewPage = selectedEmployees.length
  if (!canViewPage) {
    navigate(navigation.employees.fullPath())
  }

  if (loading) {
    return <Loading />
  }

  if (universalEditInBulk) {
    track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)

    return (
      <ResponsivePage.Body
        authorized={authorized}
        loading={loading}
        error={!selectedLegalEntityId || error}
        showHeader={false}
      >
        <NuvoImporter
          importType={nuvo_import_type}
          companyId={company.id}
          locale={locale}
          metadata={metadata}
          isEdition
        />
        <Outlet />
      </ResponsivePage.Body>
    )
  }

  return <EditInBulk filtersForm={filtersForm} employees={selectedEmployees} />
}

export default EditInBulkPage
